// pages/Shop/Shop.js - UI matched with Users Management page
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaStore,
  FaSearch,
  FaPlus,
  FaTrash,
  FaEllipsisV,
  FaEye,
  FaEdit,
  FaCheckDouble,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { RiDeleteBin5Fill } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";
import Swal from 'sweetalert2';
import axiosInstance, { BASE_URL as API_BASE } from '../../utils/axiosInstance';
import './Shop.css';

// Last loaded shops are kept, so the list shows instantly on the next visit
// while fresh data loads in the background.
const SHOPS_CACHE_KEY = 'shops_cache';

const readShopsCache = () => {
  try {
    const raw = localStorage.getItem(SHOPS_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const writeShopsCache = (list) => {
  try {
    localStorage.setItem(SHOPS_CACHE_KEY, JSON.stringify(list));
  } catch (e) {}
};

// ✅ Saari shops se unique cities
const extractCities = (list) => {
  const allCities = new Set();
  list.forEach((shop) => {
    let citiesArr = [];
    if (shop.cities) {
      if (typeof shop.cities === 'string') {
        try {
          citiesArr = JSON.parse(shop.cities);
        } catch {
          citiesArr = shop.cities.split(',').map((c) => c.trim()).filter(Boolean);
        }
      } else if (Array.isArray(shop.cities)) {
        citiesArr = shop.cities;
      }
    }
    citiesArr.forEach((c) => {
      if (c && typeof c === 'string') allCities.add(c);
    });
  });
  return Array.from(allCities).sort();
};

const Shop = () => {
  const navigate = useNavigate();

  // The saved list is shown instantly (from the very first render) while fresh data loads.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cached = readShopsCache();
    initialRef.current = { shops: cached, cities: cached ? extractCities(cached) : [] };
  }

  const [shops, setShops] = useState(initialRef.current.shops || []);
  // True only while there is nothing to show yet (so "No shops found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.shops);
  const [selectedShops, setSelectedShops] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [error, setError] = useState(null);

  // ✅ City filter dropdown (header)
  const [filterCities, setFilterCities] = useState(initialRef.current.cities);
  const [selectedFilterCity, setSelectedFilterCity] = useState(initialRef.current.cities[0] || '');

  const shopsRequestRef = useRef(0);
  const hasDataRef = useRef(!!initialRef.current.shops);
  const cityTouchedRef = useRef(false);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('/media/')) return `${API_BASE}${imagePath}`;
    return `${API_BASE}/media/${imagePath}`;
  };

  const fetchShops = useCallback(async () => {
    // Only the latest request is allowed to update the screen
    const requestId = ++shopsRequestRef.current;

    try {
      const response = await axiosInstance.get('/user/shops/');
      if (requestId !== shopsRequestRef.current) return;

      let list = response.data;
      if (list && !Array.isArray(list)) {
        list = list.results || list.data || list.shops || [];
      }
      if (!Array.isArray(list)) list = [];

      hasDataRef.current = true;
      setShops(list);
      setError(null);
      writeShopsCache(list);

      const cityList = extractCities(list);
      setFilterCities(cityList);
      // User ne city khud chuni ho to refresh us ko wapas reset na kare
      setSelectedFilterCity((prev) => {
        if (cityTouchedRef.current) {
          return prev === '' || cityList.includes(prev) ? prev : (cityList[0] || '');
        }
        return cityList[0] || '';
      });
    } catch (err) {
      if (requestId !== shopsRequestRef.current) return;
      console.error('Error fetching shops:', err);
      if (err.response?.status === 401) {
        console.error('Shop API returned 401. Token missing or expired.');
      }
      // Agar saved list dikh rahi hai to error screen nahi, wohi list rehne do
      if (!hasDataRef.current) {
        setError('Failed to load shops. Please try again.');
      }
    } finally {
      if (requestId === shopsRequestRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  useEffect(() => {
    const handleCreated = () => fetchShops();
    const handleDeleted = () => fetchShops();

    window.addEventListener('shopCreated', handleCreated);
    window.addEventListener('shopDeleted', handleDeleted);

    return () => {
      window.removeEventListener('shopCreated', handleCreated);
      window.removeEventListener('shopDeleted', handleDeleted);
    };
  }, [fetchShops]);

  const filteredShops = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return shops.filter(
      (shop) =>
        shop.shop_name?.toLowerCase().includes(q) ||
        shop.shop_email?.toLowerCase().includes(q)
    );
  }, [shops, searchTerm]);

  const handleFilterCityChange = (e) => {
    cityTouchedRef.current = true;
    setSelectedFilterCity(e.target.value);
  };

  const toggleShopSelection = (shop) => {
    const id = shop.shop_id ?? shop.id;
    setSelectedShops((prev) => {
      const isSelected = prev.some((s) => (s.shop_id ?? s.id) === id);
      if (isSelected) {
        return prev.filter((s) => (s.shop_id ?? s.id) !== id);
      } else {
        return [...prev, shop];
      }
    });
    setShowActionDropdown(false);
  };

  // ============================================================
  // Bulk Delete - per-shop loop so 1 failure doesn't
  // kill the whole batch. Shows proper success/partial/error alerts.
  // ============================================================
  const handleDeleteSelected = async () => {
    if (selectedShops.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Shops Selected',
        text: 'Please select shops to delete',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: `You are about to delete ${selectedShops.length} selected shop(s). This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#e53e3e',
      cancelButtonColor: '#5a6e8a',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Deleting Shops...',
      text: 'Please wait',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); }
    });

    // ✅ Per-shop result track (Promise.all fail pura batch kar deta tha)
    const deletedNames = [];
    const failedNames = [];

    for (const shop of selectedShops) {
      const id = shop.shop_id ?? shop.id;
      const name = shop.shop_name || `Shop #${id}`;
      try {
        await axiosInstance.delete(`/user/shops/${id}/`);
        deletedNames.push(name);
      } catch (err) {
        console.error(`Error deleting shop "${name}":`, err);
        failedNames.push(name);
      }
    }

    Swal.close();

    // Refresh list & clear selection
    await fetchShops();
    window.dispatchEvent(new CustomEvent('shopDeleted'));
    setSelectedShops([]);
    setShowActionDropdown(false);

    // ✅ Result ke hisaab se proper alert
    if (failedNames.length === 0) {
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: `${deletedNames.length} shop(s) deleted successfully!`,
        confirmButtonColor: '#004aad',
        timer: 2000,
        timerProgressBar: true
      });
    } else if (deletedNames.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Partially Deleted',
        html: `
          <p style="margin-bottom:8px;">
            <strong>${deletedNames.length}</strong> shop(s) deleted successfully.
          </p>
          <p style="color:#dc3545;margin-bottom:4px;">
            <strong>${failedNames.length}</strong> shop(s) could not be deleted:
          </p>
          <ul style="text-align:left;color:#333;font-size:0.85rem;max-height:150px;overflow:auto;padding-left:20px;">
            ${failedNames.map(n => `<li>${n}</li>`).join('')}
          </ul>
          <small style="color:#666;">In shops ke saath kuch orders/records linked hain jo delete nahi ho sake.</small>
        `,
        confirmButtonColor: '#004aad'
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        html: `
          <p style="margin-bottom:8px;">None of the shops could be deleted.</p>
          <ul style="text-align:left;color:#333;font-size:0.85rem;max-height:150px;overflow:auto;padding-left:20px;">
            ${failedNames.map(n => `<li>${n}</li>`).join('')}
          </ul>
        `,
        confirmButtonColor: '#004aad'
      });
    }
  };

  // ============================================================
  // Single Delete - backend ka detailed error show karta hai
  // ============================================================
  const handleDeleteSingle = async (shop) => {
    const shopName = shop.shop_name || 'this shop';

    const result = await Swal.fire({
      title: 'Delete Shop?',
      html: `Are you sure you want to delete <strong>${shopName}</strong>?<br/><small style="color:#dc3545;">This action cannot be undone.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: 'Deleting...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });

      const id = shop.shop_id ?? shop.id;
      await axiosInstance.delete(`/user/shops/${id}/`);

      Swal.close();

      await fetchShops();
      window.dispatchEvent(new CustomEvent('shopDeleted'));
      setSelectedShops((prev) =>
        prev.filter((s) => (s.shop_id ?? s.id) !== id)
      );

      await Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: `"${shopName}" has been deleted successfully!`,
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.close();
      console.error('Error deleting shop:', err);
      // ✅ Backend ka detailed error nikaal ke dikhao
      const backendMsg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        'Failed to delete the shop. Please try again.';
      const details = err?.response?.data?.details;
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        html: details && Array.isArray(details) && details.length > 0
          ? `<p>${backendMsg}</p>
             <ul style="text-align:left;color:#333;font-size:0.85rem;max-height:140px;overflow:auto;padding-left:20px;">
               ${details.slice(0, 8).map(d => `<li>${d}</li>`).join('')}
             </ul>`
          : backendMsg,
        confirmButtonColor: '#004aad'
      });
    }
  };

  const handleSelectAll = () => {
    if (selectedShops.length === filteredShops.length) {
      setSelectedShops([]);
    } else {
      setSelectedShops([...filteredShops]);
    }
    setShowActionDropdown(false);
  };

  const handleAddShopClick = () => {
    navigate('/add-shop');
  };

  const handleViewShop = (shop) => {
    const id = shop.shop_id ?? shop.id;
    navigate(`/shop-details/${id}`, { state: { shop } });
  };

  const handleEditShop = (shop) => {
    const id = shop.shop_id ?? shop.id;
    navigate(`/edit-shop/${id}`);
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionDropdown]);

  return (
    <div className="shop-container">
      {/* ✅ Header Section */}
      <div className="shop-header-section">
        <div className="shop-header-left">
          <FaStore className="shop-header-icon" />
          <h2 className="shop-header-title">Shops Management</h2>
          {selectedFilterCity && (
            <span className="shop-city-badge">
              <FaMapMarkerAlt className="shop-city-icon" />
              <span className="shop-city-name">
                {selectedFilterCity} ({filteredShops.length})
              </span>
            </span>
          )}
        </div>

        <div className="shop-header-right">
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={filterCities.length === 0}
            >
              <option value="">All Cities</option>
              {filterCities.map((city, index) => (
                <option key={index} value={city}>
                  {city}
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search shops..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>
              {selectedShops.length === filteredShops.length && filteredShops.length > 0
                ? 'Deselect All'
                : 'Select All'}
            </span>
          </button>

          <button className="add-shop-btn" onClick={handleAddShopClick}>
            <FaPlus />
            <span>ADD SHOP</span>
          </button>
        </div>
      </div>

      <div className="shop-content">
        {selectedShops.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedShops.length} shop(s) selected
              </span>
              <div className="action-dropdown-container">
                <button
                  className="action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button
                      className="dropdown-item delete-item"
                      onClick={handleDeleteSelected}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedShops.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button
                className="clear-selection"
                onClick={() => {
                  setSelectedShops([]);
                  setShowActionDropdown(false);
                }}
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="shop-table-container">
          {error && shops.length === 0 && !isLoading ? (
            <div className="error-state">
              <p>{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                  fetchShops();
                }}
                className="retry-btn"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="shop-table-wrapper">
              <table className="shop-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        checked={selectedShops.length === filteredShops.length && filteredShops.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Logo</th>
                    <th>Shop Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Nothing is shown until the first load is done, so "No shops found" never flashes */}
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" style={{ height: '200px' }}></td>
                    </tr>
                  ) : filteredShops.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <FaStore style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.3 }} />
                        <p>No shops found. Click "ADD SHOP" to create one.</p>
                      </td>
                    </tr>
                  ) : (
                    filteredShops.map((shop, idx) => {
                      const id = shop.shop_id ?? shop.id ?? idx;
                      const isSelected = selectedShops.some(
                        (s) => (s.shop_id ?? s.id) === (shop.shop_id ?? shop.id)
                      );
                      return (
                        <tr key={id} className={isSelected ? 'selected' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleShopSelection(shop)}
                            />
                          </td>
                          <td className="shop-logo-cell">
                            {shop.shop_logo ? (
                              <img
                                src={getImageUrl(shop.shop_logo)}
                                alt={shop.shop_name}
                                className="shop-logo-small"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                  const parent = e.target.parentElement;
                                  parent.innerHTML = `<div class="shop-logo-placeholder">${shop.shop_name?.charAt(0).toUpperCase() || 'S'}</div>`;
                                }}
                              />
                            ) : (
                              <div className="shop-logo-placeholder">
                                {shop.shop_name?.charAt(0).toUpperCase() || 'S'}
                              </div>
                            )}
                          </td>
                          <td className="shop-name-cell">{shop.shop_name}</td>
                          <td className="truncate-cell" title={shop.shop_email}>{shop.shop_email || '-'}</td>
                          <td className="truncate-cell" title={shop.shop_phone}>{shop.shop_phone || '-'}</td>
                          <td>
                            <span className={`status-badge ${shop.is_active ? 'active' : 'inactive'}`}>
                              <span>{shop.is_active ? 'Active' : 'Inactive'}</span>
                            </span>
                          </td>
                          <td className="actions-cell">
                            <div className="actions-cell-inner">
                              <button
                                className="view-shop-btn"
                                onClick={() => handleViewShop(shop)}
                                title="View Shop"
                              >
                                <FaEye />
                              </button>
                              <button
                                className="edit-shop-btn"
                                onClick={() => handleEditShop(shop)}
                                title="Edit Shop"
                              >
                                <FaEdit />
                              </button>
                              <button
                                className="delete-shop-btn"
                                onClick={() => handleDeleteSingle(shop)}
                                title="Delete Shop"
                              >
                                <RiDeleteBin5Fill />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;